/*
Muestra la página de creacion de Consignaciones, 
e invoca al preview
Autor: Benjamín León
Fecha: 2024-10-11
*/
import React, { useEffect, useState } from 'react'

import UserData from '../interfaces/UserData'
import SelectedPair from '../interfaces/SelectedPair'
import { useAuth } from '../hooks/useAuth'
import ConsignmentDto from '../interfaces/ConsignmentDto'
import { validateFields, Rule } from '../utils/validateForm'
import { dateToday } from '../utils/utils'
import { FormatProps, handleInputChangeFormat } from '../utils/handleInputChangeFormat'

import InputTextArea from './InputTextArea'
import InputField from './InputField'
import ConsignmentPreview from './ConsignmentPreview'
import { ButtonAction } from './Buttons'
import CheckboxField from './CheckBoxField'

const ConsignmentEdit: React.FC = () => {
  const { userData, selectedPair } = useAuth()
  const [formData, setFormData] = useState<ConsignmentDto | null>(null)
  const [showPreview, setShowPreview] = useState<boolean>(false)
  const [errors, setErrors] = useState<{ [key: string]: string }>({})

  useEffect(() => {
    setFormData(init(userData, selectedPair))
  }, [selectedPair, userData])

  /*
  Atrapa las modificaciones en los campos editables.
  Para los numéricos, considerar que los que se editan son los
  con _format en su nombre, y acá se editan  y se les calcula 
  su valor numérico
  */
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const formatProps: FormatProps[] = [
      { name: 'fee', name_format: 'fee_format', isDecimal: true },
      { name: 'price', name_format: 'price_format', isDecimal: false },
      { name: 'minimum', name_format: 'minimum_format', isDecimal: false },
      { name: 'charge', name_format: 'charge_format', isDecimal: false },
    ]
    handleInputChangeFormat<ConsignmentDto>(e, formData, setFormData, formatProps)
  }

  // Handle checkbox change
  const handleCheckClick = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { checked, name } = e.target
    setFormData((prevFormData) => {
      return {
        ...prevFormData,
        [name]: checked,
      }
    })
  }

  const handleEditClick = async () => {
    // Validemos antes
    // Define the fields to validate
    // EL precio puede ser CERO
    const fields = [
      //   { name: "minimum_format", value: formData?.minimum, rule: Rule.GREATER_ZERO },
      //   { name: "fee_format", value: formData?.fee, rule: Rule.GREATER_ZERO },
      { name: 'date', value: formData?.date, rule: Rule.REQUIRED },
      { name: 'city', value: formData?.city, rule: Rule.REQUIRED },
    ]

    const validationErrors = validateFields(fields)

    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors) // Set errors if any

      return
    }
    setErrors({})

    setShowPreview(true)
  }

  // The callback to go back to the edit view
  const handleGoBack = () => {
    setShowPreview(false)
  }

  return (
    <div className="p-4">
      {!showPreview ? (
        <div>
          <div className="grid grid-cols gap-4">
            <div className="grid grid-cols-2 gap-4">
              <InputField
                label="Precio cliente"
                name="price_format"
                value={formData?.price_format}
                type="string"
                handleInputChange={handleInputChange}
                errors={errors}
              />
            </div>
            <div className="grid grid-cols gap-4 border-2 p-2">
              <div className="grid grid-cols-2 gap-4">
                <div className="font-bold text-xl ">Cuadro de Pagos</div>

                <div className="ml-2">
                  <CheckboxField
                    name="paymentShow"
                    value=""
                    label="Mostrar en Vista Previa"
                    checked={formData?.paymentShow ?? false}
                    handleInputChange={handleCheckClick}
                  />
                </div>

                <div className="flex items-center space-x-4">
                  <InputField
                    label="Comisión mínima"
                    name="minimum_format"
                    value={formData?.minimum_format}
                    type="string"
                    handleInputChange={handleInputChange}
                    errors={errors}
                  />
                  <div className="mt-5">
                    <CheckboxField
                      name="minimumIva"
                      value=""
                      label="más&nbsp;IVA"
                      checked={booleanDef(formData?.minimumIva)}
                      handleInputChange={handleCheckClick}
                    />
                  </div>
                </div>
                <div className="flex items-center space-x-4">
                  <InputField
                    label="Porcentaje de comisión"
                    name="fee_format"
                    value={formData?.fee_format}
                    type="string"
                    handleInputChange={handleInputChange}
                    errors={errors}
                  />
                  <div className="mt-5">
                    <CheckboxField
                      name="feeIva"
                      value=""
                      label="más&nbsp;IVA"
                      checked={booleanDef(formData?.feeIva)}
                      handleInputChange={handleCheckClick}
                    />
                  </div>
                </div>
                <InputField
                  label="Cobro por retiro anticipado"
                  name="charge_format"
                  value={formData?.charge_format}
                  type="string"
                  handleInputChange={handleInputChange}
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <InputField
                label="Fecha"
                name="date"
                value={formData?.date}
                type="date"
                handleInputChange={handleInputChange}
                errors={errors}
              />
              <InputField
                label="Ciudad"
                name="city"
                value={formData?.city}
                type="string"
                handleInputChange={handleInputChange}
                errors={errors}
              />
            </div>
          </div>
          <div>
            <InputTextArea label="Notas" name="notes" value={formData?.notes} handleInputChange={handleInputChange} />

            <div className="flex justify-center mt-4">
              <ButtonAction label="Vista Previa" handleButtonClick={handleEditClick} />
            </div>
          </div>
        </div>
      ) : (
        <ConsignmentPreview consign={formData} onGoBack={handleGoBack} />
      )}
    </div>
  )
}

const init = (userData: UserData | null, selectedPair: SelectedPair | null): ConsignmentDto => {
  const formattedDate = dateToday()

  // Get organization defaults
  const orgDefaults = userData?.organization?.consignmentDefaults || {}

  // El profile es aca el profileId.
  // El usuario conectado es UserData.userName
  return {
    price: 0,
    price_format: '',
    // Use org defaults, then fall back to hardcoded values
    minimum: orgDefaults.minimum ?? 400000,
    minimum_format: orgDefaults.minimum ? new Intl.NumberFormat().format(orgDefaults.minimum) : '400.000',
    fee: orgDefaults.fee ?? 5,
    fee_format: orgDefaults.fee ? orgDefaults.fee.toFixed(1).replace('.', ',') : '5,0',
    charge: orgDefaults.charge ?? 0,
    charge_format: orgDefaults.charge ? new Intl.NumberFormat().format(orgDefaults.charge) : '',
    city: orgDefaults.city ?? '',
    date: formattedDate,

    notes: '',
    organization: userData?.organization?._id ?? '',
    profile: userData?.profile?._id ?? '',
    client: selectedPair?.clientId ?? '',
    vehicle: selectedPair?.vehicleId ?? '',
    paymentShow: false,
    carmap: true,
    feeIva: true,
    minimumIva: true,
  }
}

/*
devuelve el default. Si no esta o es nulo, el default es true
Esto es para que en la partida , cuando se crea uno nuevo

*/
const booleanDef = (value: boolean | null | undefined): boolean => {
  return value === undefined || value === null ? true : value
}

export default ConsignmentEdit
